import { apiStatus, nonTranslatableText } from '@va/constants';
import { requestSettings, resetRequestStatus, signDPA } from '@va/dashboard/actions/api';
import { getRequestStatus, getSettings } from '@va/dashboard/selectors/api';
import { getProviderName } from '@va/dashboard/selectors/customization';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { ModalFormWrapper } from '@va/ui/components/modals';
import { Paragraph, TextInput, fontWeights } from '@va/ui/design-system';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { default as SignatureCanvas } from 'react-signature-canvas';
import { bool, object, string } from 'yup';

enum FormFieldsNames {
  firstName = 'firstName',
  lastName = 'lastName',
  companyName = 'companyName',
  isCanvasEmpty = 'isCanvasEmpty',
}

const SignDpaModal = ({ isModalOpen, onClose }: { isModalOpen: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const canvasRef = useRef<SignatureCanvas>(null);
  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const settings = useSelector(getSettings);
  const signDpaRequestStatus = useSelector((state) => getRequestStatus(state, 'signDPA'));
  const requestSettingStatus = useSelector((state) => getRequestStatus(state, 'settings'));
  const providerName = useSelector(getProviderName);
  const dpaLink = useMemo(() => translate('all.privacyCenter.legalDocuments.dpaLink'), [translate]);

  const { values, errors, handleChange, setFieldValue, handleSubmit, resetForm, isValid, dirty } = useFormik({
    initialValues: {
      [FormFieldsNames.firstName]: settings?.firstName ?? '',
      [FormFieldsNames.lastName]: settings?.lastName ?? '',
      [FormFieldsNames.companyName]: settings?.companyName ?? '',
      [FormFieldsNames.isCanvasEmpty]: true,
    },
    validationSchema: object().shape({
      [FormFieldsNames.firstName]: string().required(translate('form.errors.requiredField')),
      [FormFieldsNames.lastName]: string().required(translate('form.errors.requiredField')),
      [FormFieldsNames.companyName]: string().required(translate('form.errors.requiredField')),
      [FormFieldsNames.isCanvasEmpty]: bool().oneOf([false], translate('form.errors.requiredField')),
    }),
    onSubmit: (values) => {
      dispatch(
        signDPA(canvasRef?.current?.toDataURL(), {
          firstName: values.firstName,
          lastName: values.lastName,
          companyName: values.companyName,
          email: settings?.email,
        }),
      );
    },
  });

  const handleClearSignature = useCallback(() => {
    canvasRef?.current?.clear();
    setFieldValue(FormFieldsNames.isCanvasEmpty, true);
  }, [setFieldValue]);

  useEffect(() => {
    if (signDpaRequestStatus === apiStatus.SUCCEEDED) {
      showSuccessNotification(2000, translate('all.privacyCenter.privacyLevels.signDpaSuccess'));
      dispatch(resetRequestStatus('signDPA'));
      dispatch(requestSettings());
      handleClearSignature();
      onClose();
    }
    if ([requestSettingStatus, signDpaRequestStatus].includes(apiStatus.FAILED)) {
      showErrorNotification();
    }
  }, [
    requestSettingStatus,
    signDpaRequestStatus,
    dispatch,
    showErrorNotification,
    showSuccessNotification,
    translate,
    onClose,
    handleClearSignature,
  ]);

  const isRequestInProgress = useMemo(
    () => [requestSettingStatus, signDpaRequestStatus].includes(apiStatus.IN_PROGRESS),
    [requestSettingStatus, signDpaRequestStatus],
  );

  return (
    <ModalFormWrapper
      title={translate('modal.signDPA.title', {
        providerName,
        dataProcessingAgreement: nonTranslatableText.dpa,
      })}
      isOpen={isModalOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      onSubmit={handleSubmit}
      isSaveButtonDisabled={!isValid || !dirty}
      saveButtonProps={{
        text: isRequestInProgress ? translate('button.isLoading') : translate('button.sign'),
        className: 'w-1/2!',
      }}
      cancelButtonProps={{
        text: translate('button.later'),
        className: 'w-1/2!',
      }}
    >
      <div className='flex flex-col p-3 gap-3'>
        <Paragraph colorClassName='text-gray-devilSolid' weight={fontWeights.semibold}>
          {translate('modal.signDPA.businessCondition', {
            dataProcessingAgreement: nonTranslatableText.dpa,
            href: dpaLink,
            className: 'terms-link',
          })}
        </Paragraph>
        <Paragraph colorClassName='text-gray-devilSolid' weight={fontWeights.semibold}>
          {translate('modal.signDPA.subtitle', {
            providerName,
            dataProcessingAgreement: nonTranslatableText.dpa,
            href: dpaLink,
            className: 'terms-link',
          })}
        </Paragraph>
        <div className='flex gap-3'>
          <div className='flex flex-col gap-3'>
            <TextInput
              required
              error={errors.firstName as string}
              name={FormFieldsNames.firstName}
              label={translate('form.placeholders.firstName')}
              onChange={handleChange}
              value={values.firstName}
              clearField={() => {
                setFieldValue(FormFieldsNames.firstName, '');
              }}
              autoFocus
            />
            <TextInput
              required
              error={errors.lastName as string}
              name={FormFieldsNames.lastName}
              label={translate('form.placeholders.lastName')}
              onChange={handleChange}
              value={values.lastName}
              clearField={() => {
                setFieldValue(FormFieldsNames.lastName, '');
              }}
            />
            <TextInput
              required
              error={errors.companyName as string}
              name={FormFieldsNames.companyName}
              label={translate('form.placeholders.companyName')}
              onChange={handleChange}
              value={values.companyName}
              clearField={() => {
                setFieldValue(FormFieldsNames.companyName, '');
              }}
            />
          </div>
          <div className='flex flex-col'>
            <SignatureCanvas
              ref={canvasRef}
              onEnd={() => setFieldValue(FormFieldsNames.isCanvasEmpty, false)}
              canvasProps={{
                width: 300,
                height: 200,
                className: 'bg-white border-[0.0625rem] border-solid border-gray rounded-12',
              }}
            />

            <Paragraph
              colorClassName='text-red-negative'
              className='text-right cursor-pointer'
              onClick={handleClearSignature}
            >
              {translate('button.clear')}
            </Paragraph>
          </div>
        </div>
      </div>
    </ModalFormWrapper>
  );
};

export default SignDpaModal;
